/**
 * 道具卡的效果类型、战阶与卡的配置校验、解锁状态。**纯函数，无 I/O。**
 *
 * 与匹配参数同一条纪律：管端写入口、发布校验、运行时装载共用这一份，各写各的必然漂移。
 *
 * ⚠️ **本文件必须零依赖**：发布校验 config-validate.ts 直接 import 它，
 * 而那个文件走 node 的 strip-only 模式加载、不能碰 domain/errors。
 *
 * 口径：《FastLudo 核心规则 v2.5》第四节 +《Fast Ludo 道具卡配置表 V4.1》：
 * 道具卡**不花钱**，只按累计胜场解锁；升阶与解锁卡**都要玩家手动点**（leo 2026-09-15）。
 *   - 胜场到了只是「可升阶」，点了升阶接口（app/battle-stage.ts）才算升上去，一次只升一阶；
 *   - 升到那一阶之后卡变成「可解锁」，再点解锁接口（app/ability.ts）才归他。
 * 接口方案见 https://portwind.jp.larksuite.com/wiki/Qc2kw7RpCia0PskxXrzjpiDjpBh
 */

/**
 * 已登记的效果类型。**服务端不执行效果。**
 *
 * 棋盘玩法的服务端不知道棋盘（docs/room-protocol-ludo.md 第一节），所以卡的效果全由客户端推：
 * 服务端只记「谁在第几手对谁用了哪张」并广播（game/room/ludo.ts 的 use_item），效果带来的位置变化
 * 走下一手的回报比对。这张白名单只有两个用途：管端发布校验（别配出客户端不认识的卡）、客户端选演出。
 *
 * 加一种效果的顺序是**客户端先实现、抬 min_client_version、再往这里登记**——反过来会让运营配出
 * 一张老客户端画不出来的卡。每种效果做什么（对谁用、动不动位置）见《局内道具卡：要定的三件事》。
 */
// 与《Fast Ludo 道具卡配置表 V4.5（定稿）》的 26 张卡一一对应，取值 = 表里 i18n_key 去掉 card. 前缀（按解锁战阶排）。
// 2026-09-14 按 leo 定一次登记齐 21 张；2026-09-30 按 V4.5 追加 21–25 五张（爪子勾 / 隐身 / 沙幕 / 回旋镖 / 箭雨）。
export const ABILITY_EFFECTS = [
  "hammer", "shield", "glue", "solvent", "rocket", "anchor", "freeze",
  "thaw", "bomb", "detector", "chain", "bulwark", "lightning", "rocket_boots",
  "cursed_die", "lucky_die", "portal", "mirror", "reverse_die", "sandstorm", "respawn_flag",
  "grapple", "vanish", "sandveil", "boomerang", "arrowrain",
] as const;
export type AbilityEffect = typeof ABILITY_EFFECTS[number];

/**
 * 每种效果是攻卡还是守卡。口径《Fast Ludo 道具卡配置表 V4.5》「道具卡配置」页「攻/守」列（攻 13 / 守 13）：
 * 攻 = 作用于对手的卡（位移攻击 / 群体位移 / 陷阱 / 控制 / 禁用 / 削弱 / 换位），守 = 作用于自己的卡
 * （防御 / 净化 / 反陷阱 / 反伤 / 反制 / 自我加速）。表里写明「新增卡必须落入其中之一」，
 * 所以攻 / 守跟着效果类型走、不另开配置列——运营选了效果就定了边，配不出一张两边都不是的卡。
 * 用处：道具格 / 转盘扇区按池子（攻 / 守 / 混）三选一（game/room/ludo-items.ts 的 offerForPool）。
 */
export type AbilitySide = "atk" | "def";
export const ABILITY_SIDE: Record<AbilityEffect, AbilitySide> = {
  hammer: "atk", shield: "def", glue: "atk", solvent: "def", rocket: "atk", anchor: "def", freeze: "atk",
  thaw: "def", bomb: "atk", detector: "def", chain: "atk", bulwark: "def", lightning: "atk", rocket_boots: "def",
  cursed_die: "atk", lucky_die: "def", portal: "atk", mirror: "def", reverse_die: "atk", sandstorm: "def", respawn_flag: "def",
  grapple: "atk", vanish: "def", sandveil: "def", boomerang: "atk", arrowrain: "atk",
};

/**
 * V4.5「选完当场生效」时 `pick_item.target` 要给什么（按「道具卡配置」页「目标类型」列）：
 *   - `enemy`：选一名对手（敌方单体 / 敌方玩家 / 传送门的「敌方单体」那一端）——给 `player_id`
 *   - `cell`：指定一格——目前没有卡用它（V4.6 起炸弹改为系统随机，见 `ahead_random`）
 *   - `ahead_random`：系统在施放者前方 1~range 格里随机一格（range 是这张卡的效果参数，ability-params.ts，默认 12）（炸弹，《Fast Ludo 道具卡配置表 V4.6》，2026-10-08 Qian 定）
 *     ——客户端**不选**；服务端出 `target.offset`，客户端从施放者当前格往前数 offset 格（服务端不知道棋盘）
 *   - `span`：连续 N 格（箭雨，N = 卡的效果参数 tiles，V5.3 默认 5）——给起始格 `cell`，范围 cell ~ cell+N-1
 *   - `none`：不用选（自己、全场随机、被动触发、全部对手）
 * 服务端只校验它校验得了的（座位存在、不是自己、没走完、不在待出场区、格号在赛道范围内）；
 * 是否被沙幕 / 隐身挡住选不中：V4.5 局由客户端算；V5.5 局（2026-10-13 起）服务端发牌时就剔除没有合法目标的单体攻卡、
 * 选卡时指向隐身 / 沙幕中的对手回 60233（ludo.ts selectableTarget）。需要目标的卡仍**可以省略目标**（= 这张卡落空）。
 */
export type AbilityTargetKind = "enemy" | "cell" | "ahead_random" | "span" | "none";
// 炸弹随机范围、箭雨覆盖格数 2026-10-09 起是卡的效果参数（ability-params.ts 的 bomb.place_ahead / arrowrain.tiles），运营可配
export const ABILITY_TARGET: Record<AbilityEffect, AbilityTargetKind> = {
  hammer: "enemy", shield: "none", glue: "none", solvent: "none", rocket: "enemy", anchor: "none", freeze: "enemy",
  thaw: "none", bomb: "ahead_random", detector: "none", chain: "enemy", bulwark: "none", lightning: "none", rocket_boots: "none",
  cursed_die: "enemy", lucky_die: "none", portal: "enemy", mirror: "none", reverse_die: "enemy", sandstorm: "none", respawn_flag: "none",
  grapple: "none", vanish: "none", sandveil: "none", boomerang: "none", arrowrain: "span",
};
export function abilityTargetOf(effectKind: string): AbilityTargetKind | null {
  return Object.hasOwn(ABILITY_TARGET, effectKind) ? ABILITY_TARGET[effectKind as AbilityEffect] : null;
}

/** 认不出的效果类型（不在白名单里的存量数据）没有边：它只会出现在混池的「随机第三张」里 */
export function abilitySideOf(effectKind: string): AbilitySide | null {
  return Object.hasOwn(ABILITY_SIDE, effectKind) ? ABILITY_SIDE[effectKind as AbilityEffect] : null;
}

/** 卡表 unlock_cost_asset 列的取值。与 game/main-assets.ts 的 AID 同值——那边零依赖不了，这里手抄一份并有守卫用例钉住 */
export const ABILITY_COST_ASSETS = ["coins", "gems", "dice"] as const;

/**
 * 升阶记录借用成就领取那张表（user_achievement / DO 的 settlement_achievement）：
 * 键是 `battle:stage` / period_key=1 / stage=阶。与活动档位的 `act:<event>` 同一个做法——
 * 成就编号的写入口不收冒号，这个命名空间运营配不出来，撞不上真成就。
 * 两处用：升阶结算（app/battle-stage.ts）、DO 提交层按键装载（do/player-claim-archive.ts）。
 */
export const BATTLE_STAGE_CLAIM_ID = "battle:stage";
export const BATTLE_STAGE_CLAIM_PERIOD = 1;

export interface AbilityCardConfig {
  cardId: string;
  name: string;
  nameI18n: Record<string, string>;
  imageUrl: string;
  effectKind: string;
  effectParams: Record<string, number | string> | null;
  unlockStage: number;
  /** 保留的列：V4.1 起卡不收费，发布校验要求 unlockCost = 0 */
  unlockCostAsset: string;
  unlockCost: number;
  sortOrder: number;
  enabled: boolean;
}

/** 升阶奖励的一项，形状与其余奖励配置同（game/main-rewards.ts 发放）。V4.1 第 5 / 10 / 15 / 20 阶送「猫狗 ×1」 */
export interface StageRewardItem { kind: string; ref?: string | null; qty?: number | null }

export interface BattleStageConfig {
  stage: number;
  name: string;
  nameI18n: Record<string, string> | null;
  winsRequired: number;
  /** 升到这一阶时发的奖励；空 = 不发 */
  rewards: StageRewardItem[];
}

const INT32_MAX = 2147483647;
const ID_RE = /^[a-z0-9_-]{1,64}$/;
/** 一阶最多几项奖励：再多就不是「升阶小礼」了，多半是配错了行 */
export const STAGE_REWARD_MAX = 10;

/**
 * 战阶表的校验。空表 = 本代理不开战阶（道具卡也就只能配 unlock_stage = 0 的）。
 * 奖励只核形状；引用对不对得上（宠物、卡包）由发布闸 checkRewardItems 核，那边有全版本的名册。
 */
export function validateBattleStages(stages: BattleStageConfig[], opts: { draft?: boolean } = {}): string[] {
  const out: string[] = [];
  const seen = new Set<number>();
  for (const s of stages) {
    if (!Number.isInteger(s.stage) || s.stage < 1) out.push(`战阶必须是 1 起的整数（当前 ${s.stage}）`);
    else if (seen.has(s.stage)) out.push(`战阶 ${s.stage} 重复`);
    else seen.add(s.stage);
    if (!Number.isInteger(s.winsRequired) || s.winsRequired < 0 || s.winsRequired > INT32_MAX) {
      out.push(`战阶 ${s.stage} 的累计胜场门槛必须是 0~${INT32_MAX} 的整数`);
    }
    const rewards = s.rewards ?? [];
    if (!Array.isArray(rewards)) out.push(`战阶 ${s.stage} 的奖励必须是列表`);
    else {
      if (rewards.length > STAGE_REWARD_MAX) out.push(`战阶 ${s.stage} 的奖励最多 ${STAGE_REWARD_MAX} 项`);
      if (rewards.some((r) => !r || typeof r.kind !== "string" || !r.kind)) out.push(`战阶 ${s.stage} 有奖励项缺类型`);
    }
  }
  /*
   * 下面两条是「整张表配完了没有」：草稿（管端实时保存）照存，发布闸拦。写入口要是拦它们，
   * 新加一阶（门槛还没填）、删掉中间一阶再补，那一页每次自动保存都 400
   */
  if (opts.draft) return out;
  // 门槛必须随阶级严格升高：不升的话「下一阶该几胜」有两个答案
  const sorted = [...stages].sort((a, b) => a.stage - b.stage);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].winsRequired <= sorted[i - 1].winsRequired) {
      out.push(`战阶 ${sorted[i].stage} 的门槛（${sorted[i].winsRequired}）必须高于第 ${sorted[i - 1].stage} 阶`);
    }
  }
  // 阶级要连号：升阶一次一阶，跳号意味着后面的阶永远升不上去
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i].stage !== i + 1) { out.push("战阶必须从 1 起连号，不能跳号"); break; }
  }
  return out;
}

/**
 * 卡表的校验。`stages` 传本版本的战阶表：卡引用的战阶必须真的存在。
 * `draft`（管端实时保存）：名称没填、引用的战阶还没建这类「还没配完」的不拦，发布闸拦
 */
export function validateAbilityCards(cards: AbilityCardConfig[], stages: BattleStageConfig[], opts: { draft?: boolean } = {}): string[] {
  const out: string[] = [];
  const stageSet = new Set(stages.map((s) => s.stage));
  const ids = new Set<string>();
  for (const c of cards) {
    if (!ID_RE.test(c.cardId)) out.push(`卡编号 ${JSON.stringify(c.cardId)} 只能是 1~64 位小写字母 / 数字 / 下划线 / 连字符`);
    else if (ids.has(c.cardId)) out.push(`卡编号 ${c.cardId} 重复`);
    else ids.add(c.cardId);

    if (c.nameI18n != null && typeof c.nameI18n !== "object") out.push(`卡 ${c.cardId} 的多语言名称格式不对`);
    else if (!opts.draft) {
      if (!c.name) out.push(`卡 ${c.cardId} 缺名称`);
      if (!c.nameI18n?.en) out.push(`卡 ${c.cardId} 的多语言名称至少要有 en`);
    }
    if (!(ABILITY_EFFECTS as readonly string[]).includes(c.effectKind)) {
      out.push(`卡 ${c.cardId} 的效果类型 ${JSON.stringify(c.effectKind)} 服务端不认识，只能是 ${ABILITY_EFFECTS.join(" / ")}`);
    }
    // 0 = 不挂阶、一开始就能解锁：只给没配战阶表的版本用（配了战阶表时发布闸 ABILITY_CARD_NO_STAGE 拦）；其余必须是本版本真配了的战阶
    if (!Number.isInteger(c.unlockStage) || c.unlockStage < 0) out.push(`卡 ${c.cardId} 的解锁战阶必须是非负整数`);
    else if (!opts.draft && c.unlockStage > 0 && !stageSet.has(c.unlockStage)) {
      out.push(`卡 ${c.cardId} 要第 ${c.unlockStage} 阶解锁，但战阶表里没有这一阶`);
    }
    if (!(ABILITY_COST_ASSETS as readonly string[]).includes(c.unlockCostAsset)) {
      out.push(`卡 ${c.cardId} 的解锁货币只能是 ${ABILITY_COST_ASSETS.join(" / ")}`);
    }
    // 《道具卡配置表 V4.1》：卡只按胜场解锁、不花钱。解锁接口不扣费，配了价格就是账面对不上
    if (c.unlockCost !== 0) out.push(`卡 ${c.cardId} 配了解锁价格 ${c.unlockCost}：道具卡按战阶免费解锁，价格必须是 0`);
    if (!Number.isInteger(c.sortOrder)) out.push(`卡 ${c.cardId} 的排序必须是整数`);
  }
  return out;
}

/**
 * 下一阶：已升到 `claimed` 阶之后该升的那阶，以及胜场够不够。满阶或没配战阶表 → null。
 * `claimable` = 胜场已到门槛、点升阶就能升。
 */
export function nextBattleStage(stages: BattleStageConfig[], claimed: number, wins: number):
  { stage: number; winsRequired: number; winsNeeded: number; claimable: boolean } | null {
  const next = [...stages].sort((a, b) => a.stage - b.stage).find((s) => s.stage > claimed);
  if (!next) return null;
  return { stage: next.stage, winsRequired: next.winsRequired, winsNeeded: Math.max(0, next.winsRequired - wins), claimable: wins >= next.winsRequired };
}

/**
 * 一张卡对某个玩家的状态。`claimedStage` 是**已升到的阶**（手动升阶），不是胜场折算的阶。
 *
 *   - `unlocked`  已拥有：有解锁记录（点过解锁）
 *   - `available` 已升到它要的阶、还没点解锁
 *   - `locked`    还没升到它要的阶
 *
 * 有记录就算拥有，哪怕战阶表后来改高了——拿到手的不收回。
 */
export function cardStatus(
  card: Pick<AbilityCardConfig, "unlockStage">, claimedStage: number, hasRow: boolean,
): "unlocked" | "available" | "locked" {
  if (hasRow) return "unlocked";
  return claimedStage >= card.unlockStage ? "available" : "locked";
}
